import React from "react";
import NextImage from "next/image";
import classnames from "classnames";
import SbEditable from "storyblok-react";
import { basicLoader } from "@/helpers/utils";
import Button from "@/components/Button/button";
import styles from "./homepage-hero.module.scss";
import type { HomepageHeroStoryblok } from "./types";

interface Props {
  blok: HomepageHeroStoryblok;
}

const HomepageHero = ({ blok }: Props) => {
  const isLeft = blok.variant === "left";
  const mobileImage = blok.background_image_mobile?.filename
    ? blok.background_image_mobile
    : null;
  // Falls back to the mobile asset so filling only that field still renders a
  // background on desktop rather than a bare gradient.
  const desktopImage = blok.background_image?.filename
    ? blok.background_image
    : mobileImage;
  // The left variant frames the subject to one side on desktop; the centered
  // homepage hero keeps the rider low in frame.
  const objectPosition = isLeft ? "70% center" : "center 85%";

  return (
    // @ts-expect-error
    <SbEditable content={blok}>
      <section
        className={classnames(styles.hero, { [styles.heroLeft]: isLeft })}
      >
        <div className={styles.heroBackground}>
          {/* desktopImage is implied by mobileImage, but testing it is what
              narrows it to non-null below. Removing it fails tsc. */}
          {mobileImage && desktopImage ? (
            // Two <NextImage>s toggled by `display` would both be fetched —
            // display:none suppresses paint, not the request — and both would
            // preload. <picture> lets the browser fetch only the matching one.
            <picture>
              {/* 64em, not 1024px - breakpoint(l) compiles to em, and px here
                  would pick the desktop asset before the desktop layout applies
                  for anyone off a 16px default. */}
              <source
                media="(min-width: 64em)"
                sizes="100vw"
                srcSet={[1440, 2880]
                  .map(
                    (w) =>
                      `${basicLoader({
                        src: desktopImage.filename as string,
                        width: w,
                      })} ${w}w`
                  )
                  .join(", ")}
              />
              {/* eslint-disable-next-line @next/next/no-img-element -- art-directed background; next/image cannot express <source media> */}
              <img
                sizes="100vw"
                srcSet={[750, 1500]
                  .map(
                    (w) =>
                      `${basicLoader({
                        src: mobileImage.filename as string,
                        width: w,
                      })} ${w}w`
                  )
                  .join(", ")}
                src={basicLoader({
                  src: mobileImage.filename as string,
                  width: 1500,
                })}
                // decorative: <source> may serve the desktop asset, so neither
                // blok's alt describes what is on screen
                alt=""
                className={styles.heroPicture}
                style={
                  {
                    "--hero-object-position-l": objectPosition,
                  } as React.CSSProperties
                }
                decoding="async"
                {...({ fetchpriority: "high" } as Record<string, string>)}
              />
            </picture>
          ) : desktopImage ? (
            <NextImage
              src={desktopImage.filename as string}
              alt={desktopImage.alt || ""}
              fill
              style={{ objectFit: "cover", objectPosition }}
              priority
              loader={basicLoader}
              sizes="100vw"
            />
          ) : null}
          <div className={styles.heroGradient} />
        </div>
        <div className={styles.heroContent}>
          {blok.eyebrow ? (
            <p className={styles.heroEyebrow}>{blok.eyebrow}</p>
          ) : null}
          {blok.heading ? (
            <h1 className={styles.heroHeading}>{blok.heading}</h1>
          ) : null}
          {blok.subheading ? (
            <p className={styles.heroSubheading}>{blok.subheading}</p>
          ) : null}
          {blok.cta_link ? (
            <div className={styles.heroActions}>
              <Button
                link={blok.cta_link}
                accent_color="accentPink"
                size="large"
                fullWidthOnMobile={isLeft}
              >
                {blok.cta_text || "Schedule a free consultation"}
              </Button>
            </div>
          ) : null}
        </div>
      </section>
    </SbEditable>
  );
};

export default HomepageHero;
